import { DOCUMENT, Injectable, inject, signal } from '@angular/core';
import {
  COMMO_ION_MODE_CLASS,
  COMMO_ION_PALETTE_DARK_CLASS,
  COMMO_THEME_CONTRACT,
  COMMO_THEME_STORAGE_KEY,
  type CommoThemePreference,
  isCommoThemePreference,
  resolveCommoThemeIsDark,
} from './theme-preference';

@Injectable({ providedIn: 'root' })
export class ThemePreferenceService {
  private readonly document = inject(DOCUMENT);

  private readonly _preference = signal<CommoThemePreference>('system');
  readonly preference = this._preference.asReadonly();

  private mediaQuery: MediaQueryList | null = null;
  private readonly onSystemChange = () => this.applyResolved();

  /** 起動時: 端末保存（なければ system）を適用し、system なら OS 変更を購読。 */
  initFromDevice(): void {
    this._preference.set(this.readStored() ?? 'system');
    this.bindSystemListener();
    this.applyResolved();
  }

  setPreference(pref: CommoThemePreference): void {
    this._preference.set(pref);
    this.writeStored(pref);
    this.bindSystemListener();
    this.applyResolved();
  }

  /** 現在の解決結果（light / dark）。UI や meta 用。FOUC boot と同式。 */
  resolvedIsDark(): boolean {
    return resolveCommoThemeIsDark(
      this._preference(),
      this.systemPrefersDark(),
    );
  }

  private bindSystemListener(): void {
    if (typeof matchMedia !== 'function') return;
    if (!this.mediaQuery) {
      this.mediaQuery = matchMedia('(prefers-color-scheme: dark)');
    }
    this.mediaQuery.removeEventListener('change', this.onSystemChange);
    if (this._preference() === 'system') {
      this.mediaQuery.addEventListener('change', this.onSystemChange);
    }
  }

  private applyResolved(): void {
    const dark = this.resolvedIsDark();
    const root = this.document.documentElement;
    // 背景トークンは `.ion-palette-dark.md`。boot と同く mode を先に確保する。
    root.classList.add(COMMO_ION_MODE_CLASS);
    root.classList.toggle(COMMO_ION_PALETTE_DARK_CLASS, dark);
    root.style.colorScheme = dark ? 'dark' : 'light';
    this.syncMeta(dark);
  }

  private syncMeta(dark: boolean): void {
    const c = COMMO_THEME_CONTRACT;
    const themeColor = this.document.querySelector('meta[name="theme-color"]');
    if (themeColor) {
      themeColor.setAttribute(
        'content',
        dark ? c.themeColorDark : c.themeColorLight,
      );
    }
    const statusBar = this.document.querySelector(
      'meta[name="apple-mobile-web-app-status-bar-style"]',
    );
    if (statusBar) {
      statusBar.setAttribute(
        'content',
        dark ? c.statusBarDark : c.statusBarLight,
      );
    }
  }

  private systemPrefersDark(): boolean {
    if (typeof matchMedia !== 'function') return false;
    return matchMedia('(prefers-color-scheme: dark)').matches;
  }

  private readStored(): CommoThemePreference | null {
    try {
      const raw = localStorage.getItem(COMMO_THEME_STORAGE_KEY);
      return isCommoThemePreference(raw) ? raw : null;
    } catch {
      return null;
    }
  }

  private writeStored(pref: CommoThemePreference): void {
    try {
      localStorage.setItem(COMMO_THEME_STORAGE_KEY, pref);
    } catch {
      /* ignore */
    }
  }
}
